'use server'

import crypto from 'crypto'
import path from 'path'
import { fileURLToPath } from 'url'

import { Attachment } from 'nodemailer/lib/mailer'

import { RECAPTCHA_ACTIONS } from '@/constants'
import ROUTES from '@/constants/routes'
import { Subscriber } from '@/database'
import {
    NewsletterVerifyEmailPlaceholders,
    NewsletterVerifyEmailTemplate,
} from '@/templates/email/newsletter-verify.email'

import action from '../handlers/action'
import handleError from '../handlers/error'
import dbConnect from '../mongoose'
import { verifyRecaptchaToken } from '../recaptcha'
import { sendEmail } from '../send-mail'
import { replaceTemplatePlaceholders } from '../utils'
import { NewsletterSubscriptionSchema } from '../validations'

export async function createSubscriber(
    recaptchaToken: string | null,
    params: CreateSubscriberParams
): Promise<ActionResponse<Subscriber>> {
    if (!recaptchaToken) {
        return handleError(
            new Error('reCAPTCHA token is missing')
        ) as ErrorResponse
    }

    const isValid = await verifyRecaptchaToken(
        recaptchaToken,
        RECAPTCHA_ACTIONS.NEWSLETTER_FORM
    )
    if (!isValid) {
        return handleError(
            new Error('reCAPTCHA verification failed')
        ) as ErrorResponse
    }

    const validationResult = await action({
        params,
        schema: NewsletterSubscriptionSchema,
    })

    if (validationResult instanceof Error) {
        return handleError(validationResult) as ErrorResponse
    }

    const { email } = validationResult.params!

    try {
        await dbConnect()

        // check if email already exists in the database
        const existingSubscriber = await Subscriber.findOne({ email })
        if (existingSubscriber) {
            if (existingSubscriber.verified) {
                return {
                    success: true,
                    data: JSON.parse(JSON.stringify(existingSubscriber)),
                }
            } else {
                // Subscription email verification is pending and still valid
                if (existingSubscriber.verifyTokenExpires > Date.now()) {
                    return {
                        success: true,
                        data: JSON.parse(JSON.stringify(existingSubscriber)),
                    }
                } else {
                    // Previous token expired, generate a new token and send a new verification email
                    const verifyToken = generateSecureToken()
                    const verifyTokenExpires = Date.now() + 24 * 60 * 60 * 1000 // 24 hours

                    existingSubscriber.verifyToken = verifyToken
                    existingSubscriber.verifyTokenExpires = verifyTokenExpires

                    await existingSubscriber.save()

                    const mailResponse = await sendVerificationEmail(
                        email,
                        verifyToken
                    )

                    if (!mailResponse?.messageId) {
                        throw new Error('Failed to send verification email')
                    }

                    return {
                        success: true,
                        data: JSON.parse(JSON.stringify(existingSubscriber)),
                    }
                }
            }
        }

        // Generate a secure token for email verification
        const verifyToken = generateSecureToken()
        const verifyTokenExpires = Date.now() + 24 * 60 * 60 * 1000 // 24 hours

        const subscriber = await Subscriber.create({
            email,
            verifyToken,
            verifyTokenExpires,
        })

        if (!subscriber) {
            throw new Error(`Failed to create subscriber`)
        }

        const mailResponse = await sendVerificationEmail(email, verifyToken)

        if (!mailResponse?.messageId) {
            throw new Error('Failed to send verification email')
        }

        return { success: true, data: JSON.parse(JSON.stringify(subscriber)) }
    } catch (error) {
        return handleError(error) as ErrorResponse
    }
}

function generateSecureToken(length = 32): string {
    const token = crypto.randomBytes(length).toString('hex')

    return token
}

async function sendVerificationEmail(email: string, verifyToken: string) {
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL

    const mailData: NewsletterVerifyEmailPlaceholders = {
        verificationLink: `${baseUrl}${ROUTES.NEWSLETTER_VERIFY(verifyToken)}`,
    }

    const mailAttachments: Attachment[] = [
        {
            filename: 'site-logo.png',
            path: path.join(
                path.dirname(fileURLToPath(import.meta.url)),
                '../..',
                'public',
                'images',
                'site-logo.png'
            ),
            cid: 'site-logo',
        },
    ]

    return await sendEmail(
        email,
        'Please confirm your email address',
        replaceTemplatePlaceholders<NewsletterVerifyEmailPlaceholders>(
            NewsletterVerifyEmailTemplate,
            mailData
        ),
        mailAttachments
    )
}
